import clsx from "clsx";
import {
  BuildingOffice2Icon,
  CalendarDaysIcon,
  DocumentTextIcon,
  LockClosedIcon,
  PencilSquareIcon,
  TagIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { MaintenanceProgressBar } from "../components/MaintenanceProgressBar";
import type { AssetStatusCounts, AssetStatusFilter } from "./assetStatus";
import "./huelleDetail.css";

export interface HuelleHeaderProps {
  title: string;
  categoryLabel: string;
  scheduleLabel: string;
  locationLabel: string;
  customerLabel: string;
  contractLabel: string;
  /** Beschriftung vor dem Projekt, z. B. „Vertrag / Projekt“ oder „Auf Zuruf / Projekt“. */
  relationLabel?: string;
  projectLabel?: string;
  projectCompleted: boolean;
  onRename?: () => void;
  counts: AssetStatusCounts;
  statusFilter: AssetStatusFilter;
  onStatusFilterChange: (next: AssetStatusFilter) => void;
}

function Fact({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  if (!value || value === "–") return null;
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5 text-sm text-label-secondary" title={label}>
      <span className="shrink-0 text-label-tertiary" aria-hidden>
        {icon}
      </span>
      <span className="truncate">{value}</span>
    </span>
  );
}

function StatusStat({
  label,
  value,
  tone,
  active,
  onClick,
}: {
  label: string;
  value: number;
  tone: "neutral" | "progress" | "ok" | "issue";
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={clsx("huelle-stat", `huelle-stat--${tone}`, active && "is-active")}
      title={active ? "Filter aufheben" : `Nur „${label}“ anzeigen`}
    >
      <span className="huelle-stat__value">{value}</span>
      <span className="huelle-stat__label">{label}</span>
    </button>
  );
}

/**
 * Kopfbereich der Wartung: Kontext (Kategorie, Zeitraum, Ort, Kunde, Vertrag) und
 * Fortschritt über alle Objekte. Die Status-Kacheln filtern die Objektliste.
 */
export function HuelleHeader({
  title,
  categoryLabel,
  scheduleLabel,
  locationLabel,
  customerLabel,
  contractLabel,
  relationLabel = "Vertrag / Projekt",
  projectLabel,
  projectCompleted,
  onRename,
  counts,
  statusFilter,
  onStatusFilterChange,
}: HuelleHeaderProps) {
  function toggle(next: AssetStatusFilter) {
    onStatusFilterChange(statusFilter === next ? "all" : next);
  }

  return (
    <section className="huelle-header">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1">
            <h2 className="truncate text-lg font-semibold text-label" title={title}>
              {title}
            </h2>
            {onRename && !projectCompleted ? (
              <button
                type="button"
                className="shrink-0 rounded-md p-1 text-label-tertiary hover:bg-fill-tertiary hover:text-label"
                aria-label="Namen der Wartung ändern"
                onClick={onRename}
              >
                <PencilSquareIcon className="h-4 w-4" aria-hidden />
              </button>
            ) : null}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
            <Fact icon={<TagIcon className="h-4 w-4" />} label="Kategorie" value={categoryLabel} />
            <Fact icon={<CalendarDaysIcon className="h-4 w-4" />} label="Zeitraum" value={scheduleLabel} />
            <Fact icon={<BuildingOffice2Icon className="h-4 w-4" />} label="Ort" value={locationLabel} />
            <Fact icon={<UserIcon className="h-4 w-4" />} label="Kunde" value={customerLabel} />
            <Fact icon={<DocumentTextIcon className="h-4 w-4" />} label={relationLabel} value={[contractLabel, projectLabel].filter(Boolean).join(" · ")} />
          </div>
        </div>
        {projectCompleted ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-status-green/15 px-2.5 py-1 text-xs font-semibold text-status-green">
            <LockClosedIcon className="h-3.5 w-3.5" aria-hidden />
            Projekt fertiggestellt – nur Ansicht
          </span>
        ) : null}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
        <div>
          {counts.total > 0 ? (
            <MaintenanceProgressBar
              completed={counts.done}
              total={counts.total}
              countLabel={`${counts.done} von ${counts.total} Objekten gewartet`}
            />
          ) : (
            <p className="text-sm text-label-secondary">
              Noch keine Objekte. Lege rechts oben das erste Objekt an – danach kannst du je Objekt die Wartung starten.
            </p>
          )}
        </div>
        {counts.total > 0 ? (
          <div className="flex flex-wrap gap-2" role="group" aria-label="Nach Status filtern">
            <StatusStat label="Offen" value={counts.open} tone="neutral" active={statusFilter === "open"} onClick={() => toggle("open")} />
            <StatusStat
              label="In Bearbeitung"
              value={counts.inProgress}
              tone="progress"
              active={statusFilter === "in_progress"}
              onClick={() => toggle("in_progress")}
            />
            <StatusStat label="Gewartet" value={counts.done} tone="ok" active={statusFilter === "done"} onClick={() => toggle("done")} />
            <StatusStat label="Mängel" value={counts.issues} tone="issue" active={statusFilter === "issues"} onClick={() => toggle("issues")} />
          </div>
        ) : null}
      </div>
    </section>
  );
}
